import { useMemo, useState } from "react";

import {
    Column,
    FONT_SIZES,
    Icon,
    Image,
    InvisibleSelect,
    Row,
    SelectOption,
    Span,
    StatColumn,
    StatProps,
    Text,
    Tooltip,
    useAppPalette
} from "@bridgesplit/ui";
import {
    combineCollections,
    filterTruthy,
    formatPercent,
    formatSeconds,
    formatUsd,
    lamportsToUiAmount,
    percentUiToDecimals,
    TIME,
    bsMath
} from "@bridgesplit/utils";
import {
    BsMetaUtil,
    formatLeverage,
    getLoopSlippage,
    getSimpleInterest,
    isLstLoop,
    isValidLeverage,
    LoopExpanded,
    LoopscalePointsAction,
    useCalculateLstBreakevenTime,
    getPositionYield,
    useUnderlyingTokenInfo
} from "@bridgesplit/abf-react";
import { useLoopscalePointStat, useOracleDiscrepencyStats } from "app/components/points/stats";
import { getWhirlpoolFromClmm, LoopRouteType } from "@bridgesplit/abf-sdk";
import { BN } from "bn.js";

import { useLoopContext } from "../LoopContext";
import { useWindContext } from "./WindContext";
import { useLoopPointsDetails } from "../../common";

const TIME_OPTIONS: SelectOption<number>[] = [
    { label: "per week", value: TIME.WEEK },
    { label: "per month", value: TIME.MONTH },
    { label: "per year", value: TIME.YEAR }
];
export default function WindNestedStats() {
    const { quote, form, isQuoteFetching, whirlpoolOffchain, oraclePrices } = useWindContext();
    const { loopExpanded } = useLoopContext();
    const pointsDetails = useLoopPointsDetails({ loopExpanded });
    const { isDarkMode } = useAppPalette();

    const [estimateSeconds, setEstimateSeconds] = useState(TIME_OPTIONS[TIME_OPTIONS.length - 1].value);

    // Get collateral yields and oracle prices for whirlpool calculations
    const collateralMints = useMemo(() => {
        if (!whirlpoolOffchain) return [];
        return [whirlpoolOffchain.tokenMintA, whirlpoolOffchain.tokenMintB].filter(filterTruthy);
    }, [whirlpoolOffchain]);

    const { collateralYields, oraclePrices: oraclePricesMap } = useUnderlyingTokenInfo(collateralMints);

    const yieldInfo = useMemo(() => {
        if (!quote || !loopExpanded) return undefined;
        const inputOracle = oraclePrices?.get(loopExpanded.inputToken.mint || "");
        const estimatedCollateralValue = form.clmmPosition
            ? form.clmmPosition.totalPrice * quote.leverageMultiplier
            : (inputOracle?.getUsdAmount(form.inputAmount ?? 0) ?? 0) * quote.leverageMultiplier;
        const estimatedPrincipalValue = loopExpanded.principalOracle.getUsdAmount(quote.principalAmount);

        let apy = quote?.netApyPct;
        const estimatedNetPositionValue = Math.max(estimatedCollateralValue - estimatedPrincipalValue, 0);

        const orcaPosition = getWhirlpoolFromClmm(form.clmmPosition);
        if (orcaPosition && quote && whirlpoolOffchain) {
            // Convert leverage multiplier to fixed-point representation to preserve precision
            const SCALE_FACTOR = 1e4;
            const scaledLeverageMultiplier = Math.round(quote.leverageMultiplier * SCALE_FACTOR);
            const leveragedLiquidity = new BN(orcaPosition.position.totalLiquidity)
                .mul(new BN(scaledLeverageMultiplier))
                .div(new BN(SCALE_FACTOR));
            // get orca position apy, and subtract the principal apy
            const orcaPositionApy = getPositionYield({
                positionLiquidity: leveragedLiquidity,
                poolLiquidity: new BN(orcaPosition.whirlpool.liquidity).add(leveragedLiquidity),
                whirlpoolOffchain,
                positionUsdValue: estimatedCollateralValue,
                tokenAAmount:
                    orcaPosition.position.liquidityAmounts[orcaPosition.tokenA.mint] /
                    10 ** orcaPosition.tokenA.decimals,
                tokenBAmount:
                    orcaPosition.position.liquidityAmounts[orcaPosition.tokenB.mint] /
                    10 ** orcaPosition.tokenB.decimals,
                tokenAMint: whirlpoolOffchain.tokenMintA,
                tokenBMint: whirlpoolOffchain.tokenMintB,
                collateralYields,
                oraclePrices: oraclePricesMap
            });
            const principalValue = loopExpanded?.principalOracle.getUsdAmount(quote.principalAmount) ?? 0;
            apy =
                (orcaPositionApy.positionYieldOverTvlAnnual * estimatedCollateralValue -
                    principalValue * quote.bestQuote.apy) /
                orcaPosition.totalPrice;
        }
        const yieldUsd = getSimpleInterest({
            principal: estimatedNetPositionValue,
            apy,
            loanDuration: estimateSeconds,
            subtractPrincipal: true
        });
        return {
            apy,
            yieldUsd
        };
    }, [
        quote,
        loopExpanded,
        oraclePrices,
        form.clmmPosition,
        form.inputAmount,
        whirlpoolOffchain,
        estimateSeconds,
        collateralYields,
        oraclePricesMap
    ]);

    const totalBorrow = (quote?.principalAmount ?? 1) * (loopExpanded?.principalOracle.usdPrice ?? 1);

    const loopRewards = useLoopscalePointStat({
        action: LoopscalePointsAction.Borrow,
        totalPrincipalAmountUsd: totalBorrow,
        variant: "body1",
        metadata: loopExpanded?.principalToken
    });

    // Only use principal token for oracle discrepancy stats as flash loan uses principal token
    const oracleDiscrepencyStats = useOracleDiscrepencyStats({
        metadata: [loopExpanded?.collateralToken].filter(filterTruthy)
    });

    const apy = yieldInfo?.apy;
    const yieldUsd = yieldInfo?.yieldUsd;

    const baseStats: StatProps[] = [
        {
            caption: "APY",
            value: formatPercent(apy),
            textColor: (() => {
                if (!apy) return "disabled";
                if (apy < 0) return "error";
                if (isDarkMode) return "success";
                return "secondary";
            })()
        },
        {
            caption: (
                <Row>
                    <Text sx={{ mr: -0.5 }} color="caption">
                        Estimated yield
                    </Text>
                    <InvisibleSelect
                        color="caption"
                        value={estimateSeconds}
                        setValue={setEstimateSeconds}
                        options={TIME_OPTIONS}
                    />
                </Row>
            ),
            value: <Text translate="no">{formatUsd(yieldUsd)}</Text>
        }
    ];

    const stats = combineCollections([baseStats, oracleDiscrepencyStats, [loopRewards]]);

    if (pointsDetails && quote) {
        const formattedLeverage = formatLeverage(quote?.leverageMultiplier);
        stats.push({
            caption: `${pointsDetails.name} rewards`,
            value: (
                <Row spacing={0.5}>
                    <Image hideSkeleton size={FONT_SIZES.body1 + "px"} variant="square" src={pointsDetails.image} />
                    <Text> {formattedLeverage} </Text>
                </Row>
            )
        });
    }

    if (loopExpanded && isLstLoop(loopExpanded)) {
        stats.push({
            caption: "Break-even in",
            value: <LstBreakEven loopExpanded={loopExpanded} />
        });
    }

    if (!form.inputAmount || !isValidLeverage(form.multiplier) || quote === null) return null;
    return <StatColumn loading={isQuoteFetching} stats={stats} />;
}

const ORCA_FEE_PERCENT = percentUiToDecimals(0.01);

function LstBreakEven({ loopExpanded }: { loopExpanded: LoopExpanded }) {
    const { externalQuote, quote: windQuote, form, oraclePrices } = useWindContext();

    const totalBorrowUsd = bsMath.mul(windQuote?.principalAmount, loopExpanded?.principalOracle.usdPrice);

    const defaultSlippage = percentUiToDecimals(getLoopSlippage(loopExpanded).defaultSlippageUi);
    const slippagePercent = form.percentSlippageDecimals ?? 0 + defaultSlippage;

    // swap fees in both directions
    const swapFeePercent = 2 * ORCA_FEE_PERCENT;
    const totalFeesPercent = swapFeePercent + slippagePercent;
    const swapAmount = lamportsToUiAmount(externalQuote?.inputAmount ?? 0, loopExpanded.principalToken.decimals);

    const maxFee = swapAmount * totalFeesPercent;

    const collateralOracle = oraclePrices?.get(loopExpanded.collateralToken.mint || "");
    const totalCollateralUsd = bsMath.mul(windQuote?.leveragedCollateralAmount, collateralOracle?.usdPrice);
    const totalContributedCollateralUsd = bsMath.mul(form.inputAmount, collateralOracle?.usdPrice);

    const maxDuration = useCalculateLstBreakevenTime({
        totalCollateralUsd,
        contributedCollateralUsd: totalContributedCollateralUsd,
        lstApy: windQuote?.netApyPct,
        loanApy: windQuote?.bestQuote.apy,
        totalDebtUsd: totalBorrowUsd
    });

    if (!externalQuote || loopExpanded.loopVault.routeType !== LoopRouteType.Jup || !windQuote || !form.inputAmount)
        return null;

    const interestPerDay = getSimpleInterest({
        principal: windQuote.principalAmount,
        apy: windQuote.bestQuote.apy,
        loanDuration: TIME.DAY,
        subtractPrincipal: true
    });

    if (!maxDuration) return null;

    return (
        <Row spacing={0.5}>
            <Text>{formatSeconds(maxDuration, { maxUnit: "day" })}</Text>
            <Tooltip
                reverseColors
                placement="bottom"
                title={
                    <Column spacing={2} p={1}>
                        <Text sx={{ display: "inline" }} color="caption" variant="body2">
                            Epoch reward accrual is estimated to offset fees and debt cost after{" "}
                            <Span sx={{ color: (theme) => theme.palette.text.primary }}>
                                {formatSeconds(maxDuration, { maxUnit: "day" })}
                            </Span>{" "}
                            of staking yield
                        </Text>

                        <StatColumn
                            variant="body2"
                            captionVariant="body2"
                            stats={[
                                { caption: "Orca LP fees", value: formatPercent(swapFeePercent) },
                                { caption: "Max slippage", value: formatPercent(slippagePercent) },
                                {
                                    caption: "Max setup cost",
                                    value: BsMetaUtil.formatAmount(loopExpanded.principalToken, maxFee)
                                },
                                {
                                    caption: "Est. daily yield",
                                    value: BsMetaUtil.formatAmount(loopExpanded.principalToken, interestPerDay)
                                }
                            ]}
                        />
                    </Column>
                }
            >
                <Text color="disabled">
                    <Icon type="tooltip" />
                </Text>
            </Tooltip>
        </Row>
    );
}
